'use client'

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClipboardDocumentCheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';

import Button from '../components/Button';
import Spinner from '../components/Spinner';
import Breadcrumbs from '../components/Breadcrumbs';
import copyId from '../utils/copyId';

import { useTemplateForm } from '../hooks/useTemplateForm';
import { useSenders } from '../hooks/useSenders';
import { useTestMessage } from '../hooks/useTestMessage';
import { breadcrumbPages } from './templateConstants';

import TemplateFields from './TemplateFields';
import SendersPanel from './SendersPanel';
import AddSenderModal from './AddSenderModal';
import TestMessageModal from './TestMessageModal';

export default function AddEditTemplate() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const {
    register, handleSubmit, errors, isSubmitting,
    setValue, watch,
    templateData, loading, policiesOptions,
    activeAccountId, dataLoading,
    onSubmit,
  } = useTemplateForm(id);

  const watchSenders = watch('senders') || [];

  const {
    showModal, setShowModal,
    selectedSenderId, setSelectedSenderId,
    selectedSenderData,
    unassignedSenderOptions, unassignedLoading,
    openAddSenderModal, addSender, removeSender, updateSender,
    roundRobinPrimaries, setPrimaryForType,
  } = useSenders({ activeAccountId, watchSenders, setValue, watchRoutingStrategy: watch('routingStrategy') });

  const {
    showTestModal, setShowTestModal,
    testToNumber, setTestToNumber,
    testMessage, setTestMessage,
    testMessageUuid, testError,
    openTestModal, sendTestMessage,
  } = useTestMessage({ templateId: id, activeAccountId });

  const handleCopyId = async (val) => {
    const ok = await copyId(val);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const waitingForAccount = !isEditMode && !activeAccountId;

  const hasSenders =
    watchSenders.length > 0 ||
    (Array.isArray(templateData?.senders) && templateData.senders.length > 0);
  const canSendTest = isEditMode && !!id && hasSenders;

  return (
    <>
      {(loading || waitingForAccount || dataLoading) && <Spinner />}

      {!loading && !waitingForAccount && (
        <div className="px-4 sm:px-6 lg:px-8">
          <Breadcrumbs pages={breadcrumbPages} />

          {/* Page header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                {isEditMode ? 'Edit Use Case' : 'Create a Use Case'}
              </h2>
              {isEditMode && (templateData?.id || id) && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <span className="font-medium">Use Case ID:</span>
                  <code className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono break-all">
                    {templateData?.id || id}
                  </code>
                  <button
                    type="button"
                    onClick={() => handleCopyId(templateData?.id || id)}
                    className="inline-flex items-center rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                    aria-label="Copy Use Case ID"
                    title={copied ? 'Copied!' : 'Copy Use Case ID'}
                  >
                    {copied
                      ? <ClipboardDocumentCheckIcon className="h-4 w-4" />
                      : <DocumentDuplicateIcon className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>

            {isEditMode && (
              <div className="shrink-0 mt-1">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={openTestModal}
                  disabled={!canSendTest}
                  title={!canSendTest ? 'Use Case must be saved and have at least one sender' : 'Send a test message'}
                >
                  Send test message
                </Button>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <TemplateFields
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              policiesOptions={policiesOptions}
              isEditMode={isEditMode}
              templateData={templateData}
              activeAccountId={activeAccountId}
            />

            <SendersPanel
              senders={watchSenders}
              errors={errors.senders}
              onUpdate={updateSender}
              onRemove={removeSender}
              onOpenAddModal={openAddSenderModal}
              routingStrategy={watch('routingStrategy')}
              roundRobinPrimaries={roundRobinPrimaries}
              onSetPrimary={setPrimaryForType}
            />

            <div className="mt-6 flex items-center justify-end gap-x-6">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/templates')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting
                  ? (isEditMode ? 'Updating...' : 'Saving...')
                  : (isEditMode ? 'Update details' : 'Save details')}
              </Button>
            </div>
          </form>
        </div>
      )}

      <AddSenderModal
        open={showModal}
        onConfirm={addSender}
        onCancel={() => setShowModal(false)}
        unassignedLoading={unassignedLoading}
        unassignedSenderOptions={unassignedSenderOptions}
        selectedSenderId={selectedSenderId}
        onSelectSender={setSelectedSenderId}
        selectedSenderData={selectedSenderData}
      />

      <TestMessageModal
        open={showTestModal}
        onCancel={() => setShowTestModal(false)}
        onConfirm={sendTestMessage}
        testToNumber={testToNumber}
        onChangeToNumber={setTestToNumber}
        testMessage={testMessage}
        onChangeMessage={setTestMessage}
        testError={testError}
        testMessageUuid={testMessageUuid}
      />
    </>
  );
}
